import { AlertTriangle, Check, Loader2 } from "lucide-react";

import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface AccessSavedIndicatorProps {
  status: SaveStatus;
  className?: string;
}

/**
 * The "changes saved automatically" cue from the design. Renders a spinner while persisting, a check when
 * saved, and a warning on failure. Idle renders nothing. Reused by every auto-saving access surface (drawer,
 * editors).
 */
export function AccessSavedIndicator({ status, className }: AccessSavedIndicatorProps) {
  const { t } = useTranslations("settings");

  if (status === "idle") return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-[11.5px]",
        status === "error" ? "text-destructive" : "text-muted-foreground",
        className,
      )}
    >
      {status === "saving" && <Loader2 size={12} className="animate-spin" />}
      {status === "saved" && <Check size={12} className="text-primary" />}
      {status === "error" && <AlertTriangle size={12} />}
      {status === "saving" && t("access_saved_saving")}
      {status === "saved" && t("access_saved_done")}
      {status === "error" && t("access_saved_error")}
    </span>
  );
}
